import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { CircleHelp, Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  RaidPlanAssignmentType,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanEncounterAssignmentCastOverride,
} from 'app/frontend/types/custom/RaidPlan'
import { v4 as uuid } from 'uuid'
import Select from 'app/frontend/base/components/Select'
import {
  getEffectiveAssignmentType,
  getSoakPrefix,
  isGeneralAssignmentType,
  isInterruptAssignmentType,
  normalizeCascadeReminders,
  normalizeCustomGrouping,
  normalizeExpectedEntities,
  normalizeOffsetReminders,
  normalizeReminderDuration,
  sanitizeCustomGroupingInput,
} from './assignmentTimeline'
import AssignmentReminderTimingFields from './AssignmentReminderTimingFields'
import TimelineAbilitySection from './TimelineAbilitySection'
import TimelineContainer from './TimelineContainer'
import EncounterAbilityLabel from './EncounterAbilityLabel'
import AssignmentTypeIcon from './AssignmentTypeIcon'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import { formatTimelineTimestamp } from './timelineMath'
import { EncounterAbilityRow } from '../hooks/useTeamPlanComputedData'
import { FormGroup } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import {
  ColorPicker,
  ColorPickerArea,
  ColorPickerContent,
  ColorPickerHueSlider,
  ColorPickerInput,
  ColorPickerSwatch,
  ColorPickerTrigger,
} from '@/components/ui/color-picker'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))
const createCastOverrideId = () => uuid().replaceAll('-', '_')

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  row: EncounterAbilityRow
  ability: RaidPlanEncounterAssignmentAbility
  spellName: string
  defaultTitle: string
  canEdit: boolean
  assignmentGroups: number
  onUpdate: (
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
}

const TeamPlanAssignmentDialog = ({
  open,
  onOpenChange,
  row,
  ability,
  spellName,
  defaultTitle,
  canEdit,
  assignmentGroups,
  onUpdate,
  onCommit,
}: Props) => {
  const { t } = useTranslation()
  const nsrtInterruptModuleId = useId()
  const { computed, zoom } = useTeamPlanTimeline()
  const [titleDraft, setTitleDraft] = useState(ability.title ?? '')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [customGroupingDraft, setCustomGroupingDraft] = useState(
    ability.custom_grouping ?? '',
  )
  const [isEditingCustomGrouping, setIsEditingCustomGrouping] = useState(false)
  const [assignmentGroupsDraft, setAssignmentGroupsDraft] = useState(
    assignmentGroups.toString(),
  )
  const [isEditingAssignmentGroups, setIsEditingAssignmentGroups] =
    useState(false)
  const [expectedEntitiesDraft, setExpectedEntitiesDraft] = useState(
    ability.expected_entities?.toString() ?? '',
  )
  const [isEditingExpectedEntities, setIsEditingExpectedEntities] =
    useState(false)
  const [cascadeDraft, setCascadeDraft] = useState(
    ability.cascade_reminders_by?.toString() ?? '',
  )
  const [isEditingCascade, setIsEditingCascade] = useState(false)
  const [offsetDraft, setOffsetDraft] = useState(
    ability.offset_reminders_by?.toString() ?? '',
  )
  const [isEditingOffset, setIsEditingOffset] = useState(false)
  const [durationDraft, setDurationDraft] = useState(
    ability.reminder_duration?.toString() ?? '',
  )
  const [isEditingDuration, setIsEditingDuration] = useState(false)
  const [draggingCastId, setDraggingCastId] = useState<string | null>(null)
  const titleColor = ability.rgb ?? row.color

  const sanitizeAssignmentGroupsInput = (value: string) =>
    value.replace(/[^\d]/g, '')

  const normalizeAssignmentGroups = (value: string) => {
    const sanitized = sanitizeAssignmentGroupsInput(value)
    if (!sanitized) return undefined
    const parsed = Number(sanitized)
    if (!Number.isInteger(parsed)) return undefined
    return Math.min(Math.max(parsed, 1), 20)
  }

  useEffect(() => {
    if (isEditingTitle) return
    setTitleDraft(ability.title ?? '')
  }, [ability.title, isEditingTitle, open])

  useEffect(() => {
    if (isEditingCustomGrouping) return
    setCustomGroupingDraft(ability.custom_grouping ?? '')
  }, [ability.custom_grouping, isEditingCustomGrouping, open])

  useEffect(() => {
    if (isEditingAssignmentGroups) return
    setAssignmentGroupsDraft(assignmentGroups.toString())
  }, [assignmentGroups, isEditingAssignmentGroups, open])

  useEffect(() => {
    if (isEditingExpectedEntities) return
    setExpectedEntitiesDraft(ability.expected_entities?.toString() ?? '')
  }, [ability.expected_entities, isEditingExpectedEntities, open])

  useEffect(() => {
    if (isEditingCascade) return
    setCascadeDraft(ability.cascade_reminders_by?.toString() ?? '')
  }, [ability.cascade_reminders_by, isEditingCascade, open])

  useEffect(() => {
    if (isEditingOffset) return
    setOffsetDraft(ability.offset_reminders_by?.toString() ?? '')
  }, [ability.offset_reminders_by, isEditingOffset, open])

  useEffect(() => {
    if (isEditingDuration) return
    setDurationDraft(ability.reminder_duration?.toString() ?? '')
  }, [ability.reminder_duration, isEditingDuration, open])

  useEffect(() => {
    if (open) return
    setDraggingCastId(null)
  }, [open])

  const visibleRange = useMemo(() => {
    const start = clamp(zoom?.start ?? 0, 0, computed.maxTime)
    const end = clamp(
      zoom?.end ?? computed.maxTime,
      start + 1,
      computed.maxTime,
    )
    return { start, end }
  }, [computed.maxTime, zoom?.end, zoom?.start])

  const updateCastOverride = useCallback(
    (
      castId: string,
      updater: (nextOverride: RaidPlanEncounterAssignmentCastOverride) => void,
      pushHistory: boolean,
    ) => {
      onUpdate((nextAbility) => {
        const nextOverrides = { ...(nextAbility.cast_overrides ?? {}) }
        const nextOverride = { ...(nextOverrides[castId] ?? {}) }
        updater(nextOverride)
        if (nextOverride.ignored === false) {
          delete nextOverride.ignored
        }
        if (
          nextOverride.time === undefined &&
          nextOverride.ignored !== true &&
          nextOverride.custom !== true
        ) {
          delete nextOverrides[castId]
        } else {
          nextOverrides[castId] = nextOverride
        }
        nextAbility.cast_overrides =
          Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined
      }, pushHistory)
    },
    [onUpdate],
  )

  const removeCastOverride = useCallback(
    (castId: string, pushHistory: boolean) => {
      onUpdate((nextAbility) => {
        const nextOverrides = { ...(nextAbility.cast_overrides ?? {}) }
        delete nextOverrides[castId]
        nextAbility.cast_overrides =
          Object.keys(nextOverrides).length > 0 ? nextOverrides : undefined
      }, pushHistory)
    },
    [onUpdate],
  )

  const onAddCastAt = useCallback(
    (at: number) => {
      if (!canEdit) return
      const resolvedPhaseIndex = computed.resolvePhaseIndexAtTime(at)
      const castId = createCastOverrideId()
      onUpdate((nextAbility) => {
        nextAbility.cast_overrides = {
          ...(nextAbility.cast_overrides ?? {}),
          [castId]: {
            custom: true,
            phase_index: resolvedPhaseIndex,
            time: Math.floor(at),
          },
        }
      }, true)
    },
    [canEdit, computed, onUpdate],
  )

  const buildCastPointerDownHandler = useCallback(
    (castId: string, isCustom: boolean) =>
      (event: React.PointerEvent<SVGRectElement>) => {
        if (event.button !== 0) return
        const svg = event.currentTarget.ownerSVGElement
        if (!svg || !canEdit) return
        event.preventDefault()
        event.stopPropagation()
        setDraggingCastId(castId)
        const rect = svg.getBoundingClientRect()
        const toTime = (clientX: number) => {
          const ratio = clamp((clientX - rect.left) / rect.width, 0, 1)
          const nextTime =
            visibleRange.start + ratio * (visibleRange.end - visibleRange.start)
          return Math.floor(clamp(nextTime, 0, computed.maxTime))
        }
        const applyTime = (clientX: number) => {
          const nextTime = toTime(clientX)
          const nextPhaseIndex = computed.resolvePhaseIndexAtTime(nextTime)
          updateCastOverride(
            castId,
            (nextOverride) => {
              if (isCustom) nextOverride.custom = true
              nextOverride.phase_index = nextPhaseIndex
              nextOverride.time = nextTime
            },
            false,
          )
        }
        const onMove = (moveEvent: PointerEvent) => applyTime(moveEvent.clientX)
        const onUp = (upEvent: PointerEvent) => {
          applyTime(upEvent.clientX)
          setDraggingCastId(null)
          onCommit()
          window.removeEventListener('pointermove', onMove)
          window.removeEventListener('pointerup', onUp)
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onUp)
      },
    [
      canEdit,
      computed,
      onCommit,
      updateCastOverride,
      visibleRange.end,
      visibleRange.start,
    ],
  )

  const castMarkers = useMemo(() => {
    if (!open) return []
    const defaultMarkerSourceEntries =
      computed.encounterAbilityRows.find(
        (encounterRow) => encounterRow.spellId === row.spellId,
      )?.entries ?? row.entries
    const defaultMarkers = defaultMarkerSourceEntries.flatMap((entry) => {
      const castId = entry.id
      const override = ability.cast_overrides?.[castId]
      if (override?.custom) return []
      const isIgnored = override?.ignored === true
      const overridePhaseIndex =
        typeof override?.phase_index === 'number' &&
        Number.isInteger(override.phase_index) &&
        override.phase_index >= 0
          ? override.phase_index
          : entry.phaseIndex
      const phaseStart = computed.phaseStarts[overridePhaseIndex] ?? 0
      const phaseEnd =
        computed.phaseEnds[overridePhaseIndex] ?? computed.maxTime
      const phaseDragMax = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
      const markerAt =
        typeof override?.time === 'number' && Number.isFinite(override.time)
          ? Math.floor(clamp(override.time, phaseStart, phaseDragMax))
          : entry.time
      return [
        {
          id: castId,
          at: markerAt,
          ignored: isIgnored,
          label: formatTimelineTimestamp(markerAt),
          disableTooltip: draggingCastId === castId,
          tooltip: `Drag to adjust time, right click to ${
            isIgnored ? 'enable' : 'ignore'
          }`,
          onContextMenu: (event: React.MouseEvent<SVGRectElement>) => {
            event.preventDefault()
            updateCastOverride(
              castId,
              (nextOverride) => {
                nextOverride.ignored = isIgnored ? undefined : true
              },
              true,
            )
          },
          onPointerDown: buildCastPointerDownHandler(castId, false),
        },
      ]
    })
    const customMarkers = Object.entries(ability.cast_overrides ?? {}).flatMap(
      ([castId, override]) => {
        if (!override.custom) return []
        const phaseIndex = override.phase_index
        if (
          typeof phaseIndex !== 'number' ||
          !Number.isInteger(phaseIndex) ||
          phaseIndex < 0
        ) {
          return []
        }
        const phaseStart = computed.phaseStarts[phaseIndex] ?? 0
        const phaseEnd = computed.phaseEnds[phaseIndex] ?? computed.maxTime
        const phaseDragMax = Math.max(phaseStart, Math.ceil(phaseEnd) - 1)
        if (
          typeof override.time !== 'number' ||
          !Number.isFinite(override.time)
        ) {
          return []
        }
        const markerAt = Math.floor(
          clamp(override.time, phaseStart, phaseDragMax),
        )
        const isIgnored = override.ignored === true
        return [
          {
            id: castId,
            at: markerAt,
            ignored: isIgnored,
            label: formatTimelineTimestamp(markerAt),
            disableTooltip: draggingCastId === castId,
            tooltip: 'Drag to adjust time, right click to remove',
            onContextMenu: (event: React.MouseEvent<SVGRectElement>) => {
              event.preventDefault()
              removeCastOverride(castId, true)
            },
            onPointerDown: buildCastPointerDownHandler(castId, true),
          },
        ]
      },
    )
    return [...defaultMarkers, ...customMarkers]
  }, [
    ability.cast_overrides,
    buildCastPointerDownHandler,
    computed.encounterAbilityRows,
    computed.maxTime,
    computed.phaseEnds,
    computed.phaseStarts,
    draggingCastId,
    open,
    removeCastOverride,
    row.entries,
    updateCastOverride,
  ])
  const activeCastMarkerCount = useMemo(
    () => castMarkers.filter((marker) => !marker.ignored).length,
    [castMarkers],
  )
  const inactiveCastMarkerCount = useMemo(
    () => castMarkers.filter((marker) => marker.ignored).length,
    [castMarkers],
  )
  const isGeneralAssignment = isGeneralAssignmentType(
    getEffectiveAssignmentType(ability.assignment_type),
  )
  const isInterruptAssignment = isInterruptAssignmentType(
    getEffectiveAssignmentType(ability.assignment_type),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type='button' variant='outline'>
          <Settings2Icon size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className='max-w-5xl'>
        {open ? (
          <>
            <DialogHeader>
              <DialogTitle asChild>
                <div className='flex items-center gap-2 min-w-0 pr-8'>
                  <span className='shrink-0'>
                    {t('dashboard.team.plans.assignments.edit-assignment')}
                  </span>
                  <AssignmentTypeIcon
                    assignmentType={ability.assignment_type}
                  />
                  <Input
                    value={titleDraft}
                    onFocus={() => setIsEditingTitle(true)}
                    onChange={(event) => {
                      const nextTitle = event.target.value
                      setTitleDraft(nextTitle)
                      onUpdate((nextAbility) => {
                        nextAbility.title =
                          nextTitle.trim() !== '' && nextTitle !== defaultTitle
                            ? nextTitle
                            : undefined
                      }, false)
                    }}
                    onBlur={() => {
                      setIsEditingTitle(false)
                      onCommit()
                    }}
                    className='h-8 font-semibold field-sizing-content min-w-50 w-fit'
                    placeholder={defaultTitle}
                    disabled={!canEdit}
                    style={{ color: titleColor }}
                    aria-label={t('dashboard.team.plans.assignments.title')}
                  />
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className='relative'>
              <div
                className={cn(
                  isGeneralAssignment &&
                    'pointer-events-none select-none opacity-30',
                )}
              >
                <TimelineAbilitySection
                  title={
                    <span className='text-sm font-medium'>
                      Click to add casts
                    </span>
                  }
                  labels={
                    <>
                      {computed.encounterAbilityRows.map((encounterRow) => (
                        <EncounterAbilityLabel
                          key={`${encounterRow.spellId}-${encounterRow.name}`}
                          spellId={encounterRow.spellId}
                          name={encounterRow.name}
                          color={encounterRow.color}
                          categories={encounterRow.categories}
                        />
                      ))}
                    </>
                  }
                  timeline={
                    <TimelineContainer
                      maxTime={computed.maxTime}
                      phaseDividers={computed.phaseDividers}
                      phaseBoundaryHandles={computed.phaseBoundaryHandles}
                      rows={computed.encounterAbilityRows}
                      disableZoomInteraction={true}
                      castMarkers={castMarkers}
                      enableAddCastInteraction={canEdit && !isGeneralAssignment}
                      onAddCastAt={onAddCastAt}
                      className='text-card'
                    />
                  }
                />
              </div>
              {isGeneralAssignment ? (
                <div className='absolute inset-0 flex items-center justify-center'>
                  <div className='rounded-md border border-border bg-background px-4 py-2 font-medium backdrop-blur-sm text-center'>
                    {t(
                      'dashboard.team.plans.assignments.general-assignment-overlay',
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {activeCastMarkerCount === 0 && !isGeneralAssignment ? (
              <Alert className='flex items-end justify-between gap-4'>
                <div>
                  <AlertTitle>This ability has no casts</AlertTitle>
                  <AlertDescription>
                    Click anywhere in the timeline above to add new casts
                    {inactiveCastMarkerCount > 0
                      ? ', or right click an inactive cast to enable it'
                      : ''}
                  </AlertDescription>
                </div>
                {canEdit ? (
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='shrink-0'
                    onClick={() =>
                      onUpdate((nextAbility) => {
                        nextAbility.assignment_type = 'general_assignment'
                      }, true)
                    }
                  >
                    {t(
                      'dashboard.team.plans.assignments.continue-without-casts',
                    )}
                  </Button>
                ) : null}
              </Alert>
            ) : null}

            <div className='space-y-1.5'>
              <div className='text-sm font-medium'>
                {t('dashboard.team.plans.assignments.reminder-settings')}
              </div>
              <Card className='p-3'>
                <FormGroup className='flex-wrap items-end'>
                  <div className='grow'>
                    <Label>
                      {t(
                        isGeneralAssignment
                          ? 'dashboard.team.plans.assignments.note-label'
                          : 'dashboard.team.plans.assignments.note-options',
                      )}
                    </Label>
                    <Input
                      value={ability.message ?? ''}
                      onChange={(event) =>
                        onUpdate((nextAbility) => {
                          nextAbility.message = event.target.value || undefined
                        }, false)
                      }
                      onBlur={onCommit}
                      className='h-8'
                      placeholder={`${getSoakPrefix(
                        spellName,
                        ability.assignment_type,
                        row.isCustom,
                      )}${spellName}`}
                      disabled={!canEdit}
                    />
                  </div>

                  <div className='grow'>
                    <Label>
                      {t('dashboard.team.plans.assignments.note-tts-options')}
                    </Label>
                    <Input
                      value={ability.tts_message ?? ''}
                      onChange={(event) =>
                        onUpdate((nextAbility) => {
                          nextAbility.tts_message =
                            event.target.value || undefined
                        }, false)
                      }
                      onBlur={onCommit}
                      className='h-8'
                      placeholder={
                        ability.message ||
                        `${getSoakPrefix(
                          spellName,
                          ability.assignment_type,
                          row.isCustom,
                        )}${spellName}`
                      }
                      disabled={!canEdit || isGeneralAssignment}
                    />
                  </div>

                  <AssignmentReminderTimingFields
                    cascadeValue={cascadeDraft}
                    offsetValue={offsetDraft}
                    durationValue={durationDraft}
                    assignmentGroups={
                      normalizeAssignmentGroups(assignmentGroupsDraft) ??
                      assignmentGroups
                    }
                    canEdit={canEdit}
                    onCascadeChange={(nextValue) => {
                      setIsEditingCascade(true)
                      setCascadeDraft(nextValue)
                      onUpdate((nextAbility) => {
                        nextAbility.cascade_reminders_by =
                          normalizeCascadeReminders(nextValue)
                      }, false)
                    }}
                    onOffsetChange={(nextValue) => {
                      setIsEditingOffset(true)
                      setOffsetDraft(nextValue)
                      onUpdate((nextAbility) => {
                        nextAbility.offset_reminders_by =
                          normalizeOffsetReminders(nextValue)
                      }, false)
                    }}
                    onDurationChange={(nextValue) => {
                      setIsEditingDuration(true)
                      setDurationDraft(nextValue)
                      onUpdate((nextAbility) => {
                        nextAbility.reminder_duration =
                          normalizeReminderDuration(nextValue)
                      }, false)
                    }}
                    onBlur={() => {
                      setIsEditingCascade(false)
                      setIsEditingOffset(false)
                      setIsEditingDuration(false)
                      setCascadeDraft(
                        normalizeCascadeReminders(cascadeDraft)?.toString() ??
                          '',
                      )
                      setOffsetDraft(
                        normalizeOffsetReminders(offsetDraft)?.toString() ?? '',
                      )
                      setDurationDraft(
                        normalizeReminderDuration(durationDraft)?.toString() ??
                          '',
                      )
                      onCommit()
                    }}
                  />

                  <div className='flex shrink-0 flex-col justify-end'>
                    <Label className='flex h-5 items-center'>
                      {t('dashboard.team.plans.assignments.color')}
                    </Label>
                    <ColorPicker
                      value={titleColor}
                      format='rgb'
                      className='flex'
                      onValueChange={(value) =>
                        onUpdate((nextAbility) => {
                          nextAbility.rgb = value
                        }, false)
                      }
                      onOpenChange={(pickerOpen) => {
                        if (pickerOpen) return
                        onCommit()
                      }}
                    >
                      <ColorPickerTrigger asChild>
                        <Button variant='ghost' size='custom' className='p-0'>
                          <ColorPickerSwatch className='hover:border-muted-foreground transition-colors w-18.5 h-8' />
                        </Button>
                      </ColorPickerTrigger>
                      <ColorPickerContent>
                        <ColorPickerArea />
                        <ColorPickerHueSlider />
                        <ColorPickerInput />
                      </ColorPickerContent>
                    </ColorPicker>
                  </div>
                </FormGroup>
              </Card>
            </div>

            <div className='space-y-1.5'>
              <div className='text-sm font-medium'>
                {t('dashboard.team.plans.assignments.assignment-settings')}
              </div>
              <Card className='p-3'>
                <FormGroup
                  className={cn(
                    'flex-wrap items-end',
                    isInterruptAssignment && 'pb-8',
                  )}
                >
                  <div className='w-36'>
                    <div className='flex items-center gap-1'>
                      <Label>
                        {t('dashboard.team.plans.assignments.custom-grouping')}
                      </Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className='inline-flex items-center text-muted-foreground'>
                            <CircleHelp className='h-3.5 w-3.5' />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className='max-w-72'>
                          {t(
                            'dashboard.team.plans.assignments.custom-grouping-tooltip',
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Input
                      value={customGroupingDraft}
                      onFocus={() => setIsEditingCustomGrouping(true)}
                      onChange={(event) => {
                        const nextValue = event.target.value
                        setCustomGroupingDraft(nextValue)
                        const sanitized = sanitizeCustomGroupingInput(nextValue)
                        const normalized = normalizeCustomGrouping(sanitized)
                        if (normalized || sanitized.trim() === '') {
                          onUpdate((nextAbility) => {
                            nextAbility.custom_grouping = normalized
                          }, false)
                        }
                      }}
                      onBlur={() => {
                        const sanitized =
                          sanitizeCustomGroupingInput(customGroupingDraft)
                        const normalized = normalizeCustomGrouping(sanitized)
                        setIsEditingCustomGrouping(false)
                        setCustomGroupingDraft(normalized ?? '')
                        onUpdate((nextAbility) => {
                          nextAbility.custom_grouping = normalized
                        }, false)
                        onCommit()
                      }}
                      placeholder={t(
                        'dashboard.team.plans.assignments.custom-grouping-placeholder',
                      )}
                      className='h-8'
                      disabled={!canEdit || isGeneralAssignment}
                    />
                  </div>
                  <div className='w-28'>
                    <Label>
                      {isGeneralAssignment
                        ? t('dashboard.team.plans.assignments.groups')
                        : t('dashboard.team.plans.assignments.groups-per-cast')}
                    </Label>
                    <Input
                      type='text'
                      inputMode='numeric'
                      maxLength={2}
                      value={assignmentGroupsDraft}
                      onFocus={() => setIsEditingAssignmentGroups(true)}
                      onChange={(event) => {
                        const nextValue = event.target.value
                        setAssignmentGroupsDraft(nextValue)
                        const sanitized =
                          sanitizeAssignmentGroupsInput(nextValue)
                        const normalized = normalizeAssignmentGroups(nextValue)
                        if (normalized || sanitized.trim() === '') {
                          onUpdate((nextAbility) => {
                            nextAbility.assignment_groups = normalized
                          }, false)
                        }
                      }}
                      onBlur={() => {
                        setIsEditingAssignmentGroups(false)
                        const normalized = normalizeAssignmentGroups(
                          assignmentGroupsDraft,
                        )
                        setAssignmentGroupsDraft(normalized?.toString() ?? '')
                        onUpdate((nextAbility) => {
                          nextAbility.assignment_groups = normalized
                        }, false)
                        onCommit()
                      }}
                      className='h-8'
                      disabled={!canEdit}
                    />
                  </div>
                  <div className='w-40'>
                    <Label>
                      {t(
                        'dashboard.team.plans.assignments.expected-player-count',
                      )}
                    </Label>
                    <Input
                      type='text'
                      inputMode='numeric'
                      maxLength={2}
                      value={expectedEntitiesDraft}
                      onFocus={() => setIsEditingExpectedEntities(true)}
                      onChange={(event) => {
                        const nextValue = event.target.value
                        setExpectedEntitiesDraft(nextValue)
                        onUpdate((nextAbility) => {
                          nextAbility.expected_entities =
                            normalizeExpectedEntities(nextValue)
                        }, false)
                      }}
                      onBlur={() => {
                        setIsEditingExpectedEntities(false)
                        const normalized = normalizeExpectedEntities(
                          expectedEntitiesDraft,
                        )
                        setExpectedEntitiesDraft(normalized?.toString() ?? '')
                        onUpdate((nextAbility) => {
                          nextAbility.expected_entities = normalized
                        }, false)
                        onCommit()
                      }}
                      className='h-8'
                      disabled={!canEdit}
                    />
                  </div>
                  <div className='relative min-w-48 flex-1'>
                    <Label>
                      {t('dashboard.team.plans.assignments.assignment-type')}
                    </Label>
                    <Select
                      value={getEffectiveAssignmentType(
                        ability.assignment_type,
                      )}
                      setValue={(value) => {
                        const nextType = (value ??
                          'general') as RaidPlanAssignmentType
                        onUpdate((nextAbility) => {
                          nextAbility.assignment_type =
                            nextType === 'general' ? undefined : nextType
                          if (!isInterruptAssignmentType(nextType)) {
                            nextAbility.nsrt_interrupt_module = undefined
                          }
                        }, true)
                      }}
                      options={[
                        {
                          value: 'general',
                          sortLabel: '0',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.general',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.general',
                          ),
                        },
                        {
                          value: 'general_assignment',
                          sortLabel: '1',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.general-assignment',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.general-assignment',
                          ),
                        },
                        {
                          value: 'immunity',
                          sortLabel: '2',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.immunity',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.immunity',
                          ),
                        },
                        {
                          value: 'mobile_immunity',
                          sortLabel: '3',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.mobile-immunity',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.mobile-immunity',
                          ),
                        },
                        {
                          value: 'area_control',
                          sortLabel: '4',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.area-control',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.area-control',
                          ),
                        },
                        {
                          value: 'interrupt',
                          sortLabel: '5',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.interrupt',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.interrupt',
                          ),
                        },
                        {
                          value: 'ranged_interrupt',
                          sortLabel: '6',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.ranged-interrupt',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.ranged-interrupt',
                          ),
                        },
                        {
                          value: 'melee_interrupt',
                          sortLabel: '7',
                          label: t(
                            'dashboard.team.plans.assignments.assignment-types.melee-interrupt',
                          ),
                          description: t(
                            'dashboard.team.plans.assignments.assignment-type-descriptions.melee-interrupt',
                          ),
                        },
                      ]}
                      inModal
                      hideIcon
                      className='h-8'
                      disabled={!canEdit}
                    />
                    {isInterruptAssignment ? (
                      <div className='absolute top-full left-0 mt-2 flex items-center gap-2'>
                        <Checkbox
                          id={nsrtInterruptModuleId}
                          checked={ability.nsrt_interrupt_module === true}
                          disabled={!canEdit}
                          onCheckedChange={(checked) => {
                            const nextValue = checked === true
                            onUpdate((nextAbility) => {
                              nextAbility.nsrt_interrupt_module =
                                nextValue || undefined
                              if (nextValue) {
                                nextAbility.view_mode = 'all_together'
                              }
                            }, true)
                          }}
                        />
                        <Label htmlFor={nsrtInterruptModuleId}>
                          {t(
                            'dashboard.team.plans.assignments.use-nsrt-interrupt-module',
                          )}
                        </Label>
                      </div>
                    ) : null}
                  </div>
                </FormGroup>
              </Card>
            </div>

            <div className='justify-end flex gap-2'>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                Continue
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default TeamPlanAssignmentDialog
